import * as Phaser from 'phaser';

type GridPosition = {
    row: number;
    col: number;
};

type Position = {
    occupant: Phaser.GameObjects.Sprite | null;
    dropzone: Phaser.GameObjects.Sprite | null;
    coordinates: {
        x: number;
        y: number;
    };
    possibleMoves: [number, number][];
};

// Every cell connected by a line on the board, keyed by "row,col".
const BOARD_LINES: Record<string, [number, number][]> = {
    '0,0': [[0, 1], [1, 0], [1, 1]],
    '0,1': [[0, 0], [0, 2], [1, 1]],
    '0,2': [[0, 1], [1, 2], [1, 1]],
    '1,0': [[0, 0], [2, 0], [1, 1]],
    '1,1': [[0, 0], [0, 1], [0, 2], [1, 0], [1, 2], [2, 0], [2, 1], [2, 2]],
    '1,2': [[0, 2], [2, 2], [1, 1]],
    '2,0': [[2, 1], [1, 0], [1, 1]],
    '2,1': [[2, 0], [2, 2], [1, 1]],
    '2,2': [[2, 1], [1, 2], [1, 1]],
};

// Every possible 3-cell line on the board: rows, columns and both diagonals.
const WINNING_LINES: [number, number][][] = [
    [[0, 0], [0, 1], [0, 2]],
    [[1, 0], [1, 1], [1, 2]],
    [[2, 0], [2, 1], [2, 2]],
    [[0, 0], [1, 0], [2, 0]],
    [[0, 1], [1, 1], [2, 1]],
    [[0, 2], [1, 2], [2, 2]],
    [[0, 0], [1, 1], [2, 2]],
    [[0, 2], [1, 1], [2, 0]],
];

export class Board {
    boardBase: Phaser.GameObjects.Image;
    positions: Position[][] = [];
    lastPiecePosition: GridPosition;
    lockedBy: Map<string, number> = new Map();

    constructor(boardBase: Phaser.GameObjects.Image) {
        this.boardBase = boardBase;
        this.lastPiecePosition = { row: 0, col: 0 };

        for (let row = 0; row < 3; row++) {
            this.positions[row] = [];
            for (let col = 0; col < 3; col++) {
                this.positions[row][col] = {
                    occupant: null,
                    dropzone: null,
                    coordinates: {
                        x: this.boardBase.x + (col * (this.boardBase.displayWidth / 2)),
                        y: this.boardBase.y + (row * (this.boardBase.displayHeight / 2)),
                    },
                    possibleMoves: BOARD_LINES[`${row},${col}`],
                };
            }
        }
    }

    getPosition(row: number, col: number): Position {
        return this.positions[row][col];
    }

    isPositionOccupied(row: number, col: number): boolean {
        return this.positions[row][col].occupant !== null;
    }

    getIndexOfPiece(piece: Phaser.GameObjects.Sprite): GridPosition {
        for (let row = 0; row < 3; row++) {
            for (let col = 0; col < 3; col++) {
                if (this.positions[row][col].occupant === piece) {
                    return { row, col };
                }
            }
        }
        return { row: 0, col: 0 };
    }

    canMove(fromRow: number, fromCol: number, toRow: number, toCol: number): boolean {
        const from = this.positions[fromRow][fromCol];
        const isConnected = from.possibleMoves.some(([row, col]) => row === toRow && col === toCol);
        const isDestinationFree = this.positions[toRow][toCol].occupant === null;
        const isDestinationUnlocked = !this.isCellLocked(toRow, toCol);
        return isConnected && isDestinationFree && isDestinationUnlocked;
    }

    lockCell(row: number, col: number, owner: number): void {
        this.lockedBy.set(`${row},${col}`, owner);
    }

    unlockCell(row: number, col: number): void {
        this.lockedBy.delete(`${row},${col}`);
    }

    unlockByOwner(owner: number): void {
        for (const [key, value] of Array.from(this.lockedBy.entries())) {
            if (value === owner) {
                this.lockedBy.delete(key);
            }
        }
    }

    getCellsLockedBy(owner: number): string[] {
        return Array.from(this.lockedBy.entries())
            .filter(([, lockedByOwner]) => lockedByOwner === owner)
            .map(([key]) => key);
    }

    isCellLocked(row: number, col: number): boolean {
        return this.lockedBy.has(`${row},${col}`);
    }

    getLockedCells(): string[] {
        return Array.from(this.lockedBy.keys());
    }

    clearLocks(): void {
        this.lockedBy.clear();
    }

    getPlayerPieces(player: number): GridPosition[] {
        const result: GridPosition[] = [];
        for (let row = 0; row < 3; row++) {
            for (let col = 0; col < 3; col++) {
                const occupant = this.positions[row][col].occupant;
                if (occupant && occupant.getData('player') === player) {
                    result.push({ row, col });
                }
            }
        }
        return result;
    }

    hasWon(player: number): boolean {
        const occupied = new Set(this.getPlayerPieces(player).map(({ row, col }) => `${row},${col}`));
        // Player 1 starts on the bottom row (2), Player 2 on the top row (0).
        const startingRow = player === 1 ? 2 : 0;

        return WINNING_LINES.some((line) => {
            // A line made up of the pieces' original starting positions does not count.
            if (line.every(([row]) => row === startingRow)) {
                return false;
            }
            return line.every(([row, col]) => occupied.has(`${row},${col}`));
        });
    }

    hasLegalMove(player: number): boolean {
        for (const { row, col } of this.getPlayerPieces(player)) {
            for (const [toRow, toCol] of this.positions[row][col].possibleMoves) {
                if (this.positions[toRow][toCol].occupant === null && !this.isCellLocked(toRow, toCol)) {
                    return true;
                }
            }
        }
        return false;
    }
}
